import { useCallback, useEffect, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReportingDashboardPanel from './Panels/ReportDashboardPanel/ReportingDashboardPanel';
import { useReportingPageStyles } from './ReportingPage.styles';
import ReportingPanel from './Panels/ReportingPanel/ReportingPanel';
import BroadcastMessageReportPanel from './Panels/BroadcastMessagePanel/BroadcastMessageReportPanel';
import ResponsiveTabList from '../../controls/ResponsiveTabList/ResponsiveTabList';
import useResponsiveTabList, {
    ResponsiveTab,
} from '../../controls/ResponsiveTabList/useResponsiveTabList';
import { useSearchParams } from 'react-router';
import { useAppDispatch, useSelector } from '../../store/hooks';
import {
    lockGroundTeamId,
    setGroundTeamId,
} from '../../store/features/reportingDashboard/reportingDashboardSlice';
import { useAppContext } from '../../model';
import {
    Toast,
    ToastBody,
    Toaster,
    ToastIntent,
    ToastTitle,
    useToastController,
} from '@fluentui/react-components';

type TabId = 'reportingDashboard' | 'reports' | 'broadcast';

export const reportingTabsPageParamKeys = {
    allTeamsInDashboard: 'allTeamsInDashboard',
} as const;

const ReportCreationToaster = () => {
    const { t } = useTranslation();

    const { lastReportCreationFailure, lastReportCreationSuccess } =
        useSelector((x) => x.reportingBoard);

    const toasterId = useId();
    const { dispatchToast } = useToastController(toasterId);

    const dispatchCreationResult = useCallback(
        (body: string, intent: ToastIntent) => {
            dispatchToast(
                <Toast>
                    <ToastTitle>
                        {t('Reporting_ReportCreationTitle')}
                    </ToastTitle>
                    <ToastBody>{body}</ToastBody>
                </Toast>,
                { intent }
            );
        },
        [dispatchToast, t]
    );

    useEffect(() => {
        if (lastReportCreationFailure === undefined) {
            return;
        }

        dispatchCreationResult(t('Reporting_ReportCreationFailure'), 'error');
    }, [lastReportCreationFailure, dispatchCreationResult, t]);

    useEffect(() => {
        if (lastReportCreationSuccess === undefined) {
            return;
        }

        dispatchCreationResult(t('Reporting_ReportCreationSuccess'), 'success');
    }, [lastReportCreationSuccess, dispatchCreationResult, t]);

    return <Toaster toasterId={toasterId} />;
};

const ReportingTabsPage = () => {
    const { t } = useTranslation();
    const styles = useReportingPageStyles();

    const dispatch = useAppDispatch();

    const [searchParams] = useSearchParams();
    const allTeamsInDashboard = !!searchParams.get(
        reportingTabsPageParamKeys.allTeamsInDashboard
    );

    const teamId = useAppContext().teamsContext.team?.groupId ?? '';

    useEffect(() => {
        if (!allTeamsInDashboard) {
            dispatch(lockGroundTeamId(teamId));
        } else {
            dispatch(setGroundTeamId(null));
        }
    }, [dispatch, allTeamsInDashboard, teamId]);

    const tabs = useMemo<ResponsiveTab<TabId>[]>(
        () => [
            {
                id: 'reportingDashboard',
                label:
                    t('Configuration_Tab_BA_Reporting_Dashboard_DisplayName') ||
                    'Reporting Dashboard',
                content: <ReportingDashboardPanel />,
            },
            {
                id: 'broadcast',
                label:
                    t(
                        'Configuration_Tab_BA_Broadcast_Message_Report_DisplayName'
                    ) || 'Broadcast Report',
                content: <BroadcastMessageReportPanel />,
            },
            {
                id: 'reports',
                label:
                    t('Configuration_Tab_BA_Reporting_History_DisplayName') ||
                    'Reports',
                content: <ReportingPanel />,
            },
        ],
        [t]
    );

    const { selectedTab, setSelectedTabId } = useResponsiveTabList(
        tabs,
        'reportingDashboard'
    );

    return (
        <div className={styles.root}>
            <div>
                <ResponsiveTabList
                    items={tabs}
                    selectedId={selectedTab.id}
                    onTabSelect={setSelectedTabId}
                />
            </div>
            {selectedTab.content}
            <ReportCreationToaster />
        </div>
    );
};

export default ReportingTabsPage;
